import { useLocation } from '@uhg-abyss/web/tools/reactRouterTools';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import React from 'react';

import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { DateTime } from '../../../../utils/DateTime';
import { shouldUseGehaV2Styling } from '../../../../utils/gehaThemeUtils';
import {
  getCoverageTypes,
  getCurrentMember,
} from '../../../../utils/user.utils';
import { getGuestPlanSelectionData } from '../../../GuestExperience/Guest.utils';
import {
  TimeStampContentContainer,
  TimeStampContentHeadingStyle,
  TimeStampContentSubTitleStyle,
  TimeStampDate,
} from './TimeStampContent.styled';

function getFormattedDate(): string {
  return DateTime.getCurrentDateTime().toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

function filterTimestampItems(
  items: any[],
  coverageContext: string | null,
  memberCoverages: any[]
): any[] {
  if (!items || items.length === 0) {
    return items;
  }

  const coverageKeys = {
    M: '0',
    B: '1',
    V: '2',
    D: '3',
  };

  const matchesCoverage = (item: any, coverage: string): boolean =>
    String(item?.key) === coverageKeys[coverage];

  if (!isEmpty(memberCoverages)) {
    return items.filter((item) =>
      memberCoverages.some((cov) => matchesCoverage(item, cov))
    );
  } else {
    if (!coverageContext || !coverageKeys[coverageContext]) {
      return items;
    }

    return items.filter((item) => matchesCoverage(item, coverageContext));
  }
}

export const TimeStampContent = () => {
  const formattedDate = getFormattedDate();
  const data = useLagoon('landing-info')();

  const [enableTimestampMemberCoverage] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIMESTAMP_MEMBER_COVERAGE,
  ]);

  const [enableTimestampGuestPrintDirectory] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIMESTAMP_GUEST_PRINT_DIRECTORY,
  ]);

  const location = useLocation();
  const coverageTypeFromUrl =
    new URLSearchParams(location.search).get('coverageType') || null;

  // On /print-directory the coverageType isn't in the URL; read the guest's
  // selected coverage from session-scoped plan selection data instead.
  const isPrintDirectoryPage =
    enableTimestampGuestPrintDirectory &&
    location.pathname?.includes('print-directory');
  const coverageType =
    coverageTypeFromUrl ||
    (isPrintDirectoryPage
      ? getGuestPlanSelectionData()?.coverageTypeCode
      : null);

  const currentMember = getCurrentMember();
  const allMemberCoverages = getCoverageTypes(currentMember);

  // Only use member coverages if the feature flag is enabled
  const memberCoverages = enableTimestampMemberCoverage
    ? allMemberCoverages
    : [];

  const timeStampLabel = find(data, {
    Timestamp_key: ConstantsLagoon.LANDING_INFO.TIME_STAMP_CONTENT,
  });

  const timeStampContent = data.filter(
    (item) => item.Timestamp_key !== 'TIME_STAMP_CONTENT'
  );

  const filteredTimeStampContent = filterTimestampItems(
    timeStampContent,
    coverageType,
    memberCoverages
  );

  const isGEHAV2 = shouldUseGehaV2Styling();

  return (
    <React.Fragment>
      <TimeStampContentContainer cssProps={{ isGEHAV2 }}>
        <Text
          data-auto-testid="directory-update-timestamp"
          data-testid="directory-update-timestamp"
        >
          <TimeStampContentHeadingStyle cssProps={{ isGEHAV2 }}>
            {timeStampLabel?.TimeStamp_Header}
          </TimeStampContentHeadingStyle>
          <TimeStampContentSubTitleStyle cssProps={{ isGEHAV2 }}>
            {timeStampLabel?.Timestamp_Value}
          </TimeStampContentSubTitleStyle>
        </Text>
        <div
          className="date-info"
          data-auto-testid="directory-last-updated-container"
          data-testid="directory-last-updated-container"
        >
          {filteredTimeStampContent?.map((item, index) => (
            <TimeStampDate cssProps={{ isGEHAV2 }} key={index}>
              <span className="header">{item?.TimeStamp_Header}</span>{' '}
              {formattedDate}
            </TimeStampDate>
          ))}
        </div>
      </TimeStampContentContainer>
    </React.Fragment>
  );
};
